import axios from "axios";
import parse from "html-react-parser";
import { useEffect, useMemo, useState } from "react";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import ModalEccomerce from "src/components/molecules/ModalEcommerce";
import useSWR from "swr";

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API ||
  "";

const IMAGE_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_IMAGE_BASE_URL) ||
  process.env.REACT_APP_IMAGE ||
  process.env.REACT_APP_ASSET_BASE_URL ||
  "";

const PER_PAGE = 12;
const DEFAULT_SORT = "date";
const PAGINATION_WINDOW = 2;

const fetcher = (url) => axios.get(url).then((r) => r.data);

const joinImageURL = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || /^\/\//.test(path)) return path;
  if (!base) return path.startsWith("/") ? path : `/${path}`;
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

const pickImageField = (obj = {}) => {
  const pick = (v) => (typeof v === "string" ? v : v?.url || v?.src || "");
  return (
    pick(obj.image) ||
    pick(obj.image_url) ||
    pick(obj.thumbnail) ||
    pick(obj.logo) ||
    pick(obj.cover) ||
    pick(obj.icon) ||
    pick(obj.icon_url) ||
    ""
  );
};

const getImageUrl = (raw) =>
  raw ? joinImageURL(IMAGE_BASE, raw) : "/images/placeholders/category.png";

const onImgError = (e) => {
  if (e.currentTarget.dataset.fallbackApplied) return;
  e.currentTarget.dataset.fallbackApplied = "1";
  e.currentTarget.src = "/images/placeholders/category.png";
};

const PerCategory = () => {
  const location = useLocation();

  const [sort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  const categoryId = location?.state?.id ?? null;

  const query =
    categoryId != null
      ? `?category=${categoryId}&order_by=${sort}&page=${page}&per_page=${PER_PAGE}`
      : null;

  const { data, error, isLoading } = useSWR(
    query ? `${API_BASE}/product/filter${query}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const products = data?.products?.data || [];
  const prevPage = data?.products?.prev_page_url;
  const nextPage = data?.products?.next_page_url;
  const currentPage = data?.products?.current_page || 1;
  const lastPage = data?.products?.last_page || 1;

  const [isOpen, setIsOpen] = useState(false);
  const [listing, setListing] = useState(null);

  const handleOpenModal = (d) => {
    setIsOpen(true);
    setListing(d);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const pagesWindow = useMemo(() => {
    const pages = [];
    const start = Math.max(1, currentPage - PAGINATION_WINDOW);
    const end = Math.min(lastPage, currentPage + PAGINATION_WINDOW);
    if (start > 1) pages.push(1, "…");
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < lastPage) pages.push("…", lastPage);
    return pages;
  }, [currentPage, lastPage]);

  const getProductImage = (item) =>
    getImageUrl(pickImageField(item) || pickImageField(item?.images?.[0]) || "");

  const parsedCategoryDescription = useMemo(() => {
    const html = data?.category?.description || "";
    return parse(html);
  }, [data?.category?.description]);

  return (
    <>
      <div className="max-w-screen-xl mx-auto px-4 pt-5 md:pt-10">
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
            Gagal memuat data kategori. Coba muat ulang halaman.
          </div>
        )}

        {data?.category?.description && (
          <div className="prose prose-description max-w-none text-slate-700 text-sm sm:text-base leading-7">
            {parsedCategoryDescription}
          </div>
        )}

        <div className="mt-6 border-t border-slate-200" />
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-6 sm:py-8 md:py-10">
        {isLoading && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="p-3">
                  <div className="aspect-[16/9] rounded-lg bg-slate-100" />
                </div>
                <div className="px-4 py-3 border-t">
                  <div className="h-3 w-20 bg-slate-200 rounded mx-auto" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !products.length && !error && (
          <div className="py-10 text-center">
            <span className="text-base sm:text-lg text-red-500">
              Data Not Found!
            </span>
          </div>
        )}

        {!!products.length && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((item) => (
              <button
                key={item.id}
                onClick={() => handleOpenModal(item.produk_listing ?? item)}
                className="
                  text-left rounded-xl border border-slate-200 bg-white
                  shadow-sm overflow-hidden
                  touch-manipulation active:scale-[0.98]
                  hover:shadow-md transition
                "
                title={item.name}
              >
                <div className="p-3">
                  <div className="aspect-[16/9] rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                    <img
                      src={getProductImage(item)}
                      alt={item.name || "Product"}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onError={onImgError}
                    />
                  </div>
                </div>

                <div className="px-3 py-3 border-t text-center font-semibold text-xs sm:text-sm uppercase text-slate-800">
                  {item?.name || "Product"}
                </div>
              </button>
            ))}
          </div>
        )}

        {!!products.length && lastPage > 1 && (
          <div className="mt-10 flex flex-wrap justify-center items-center gap-1">
            <button
              onClick={() => prevPage && setPage(currentPage - 1)}
              disabled={!prevPage}
              className={`h-10 w-10 flex items-center justify-center rounded-full text-lg ${
                prevPage
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-300 cursor-not-allowed"
              }`}
            >
              <BsChevronLeft />
            </button>

            {pagesWindow.map((p, i) =>
              p === "…" ? (
                <span
                  key={`ellipsis-${i}`}
                  className="px-2 text-slate-400 select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`h-10 min-w-[40px] px-3 rounded-full text-sm ${
                    p === currentPage
                      ? "bg-emerald-100 text-emerald-700 font-semibold"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => nextPage && setPage(currentPage + 1)}
              disabled={!nextPage}
              className={`h-10 w-10 flex items-center justify-center rounded-full text-lg ${
                nextPage
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-slate-300 cursor-not-allowed"
              }`}
            >
              <BsChevronRight />
            </button>
          </div>
        )}
      </div>

      <ModalEccomerce isOpen={isOpen} setIsOpen={setIsOpen} data={listing} />
    </>
  );
};

export default PerCategory;
